import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../../api/apiClient';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import SharedHeader from '../../../components/shared/SharedHeader';
import RevenueSongTable from './RevenueSongTable';
import { toast } from '../../../components/shared/toast';
import './RevenueArtistSongs.css';

function RevenueArtistSongs({ artistId, artistName, onBack }) {
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const songsPerPage = 20;

  const fetchArtistSongs = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiClient(`/revenue/artist/${artistId}/songs`);
      if (res && res.songs) {
        setSongs(res.songs);
      }
    } catch (err) {
      console.error('Error loading artist songs:', err);
      toast.error('Failed to load artist songs');
    } finally {
      setIsLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    if (artistId) fetchArtistSongs();
  }, [artistId, fetchArtistSongs]);

  const filteredSongs = useMemo(() => {
    if (!searchQuery.trim()) return songs;
    const q = searchQuery.toLowerCase();
    return songs.filter(s =>
      (s.name && s.name.toLowerCase().includes(q)) ||
      (s.artist && s.artist.toLowerCase().includes(q)) ||
      (s.nameSinhala && s.nameSinhala.toLowerCase().includes(q)) ||
      (s.isrcCode && s.isrcCode.toLowerCase().includes(q))
    );
  }, [songs, searchQuery]);

  return (
    <div className="revenue-artist-songs-wrapper">
      <LoadingOverlay isLoading={isLoading} message="Loading Artist Songs..." />

      {/* Back Button + Header */}
      <div className="ras-back-section">
        <button
          type="button"
          className="ras-back-btn"
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#64748b',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px 0',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.color = '#0b66e3'}
          onMouseLeave={(e) => e.target.style.color = '#64748b'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to {artistName}
        </button>
      </div>

      {/* Shared Control Header */}
      <div style={{ position: 'relative' }}>
        <SharedHeader
          title=""
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search songs..."
          resultCount={filteredSongs.length}
          resultLabel="Songs"
          showSortFilter={false}
        />
      </div>

      {/* Songs Table */}
      {filteredSongs.length === 0 ? (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          padding: '40px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '16px', fontWeight: '600', color: '#64748b' }}>
            No songs found for {artistName}
          </p>
        </div>
      ) : (
        <RevenueSongTable
          songs={filteredSongs}
          currentPage={currentPage}
          totalSongs={filteredSongs.length}
          songsPerPage={songsPerPage}
          onPageChange={setCurrentPage}
          onSelectSong={null}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

export default RevenueArtistSongs;
